import { useEffect, useState } from "react";
import { formatPressure, formatTemp, formatWear } from "../utils/formatters";
import type { TireData, TireCorner } from "../types";

interface Props {
  tireData: TireData | null;
  unit?: string;
}

/* ═══════════════════════════════════════════════════════════════════════════
   Constants
   ═══════════════════════════════════════════════════════════════════════════ */

const CORNERS = ["LF", "RF", "LR", "RR"] as const;
type CornerKey = (typeof CORNERS)[number];

/** Pit-stop stagger delays (ms) — LF first, RF, LR, RR last */
const STAGGER_DELAY: Record<CornerKey, number> = { LF: 0, RF: 120, LR: 240, RR: 360 };

/** Grid placement matching real car layout */
const GRID_POS: Record<CornerKey, { row: number; col: number }> = {
  LF: { row: 1, col: 1 },
  RF: { row: 1, col: 2 },
  LR: { row: 2, col: 1 },
  RR: { row: 2, col: 2 },
};

/* Temperature thresholds for heatmap gradient (Fahrenheit baseline) */
const TEMP_STOPS_F = [
  { temp: 100, color: [0, 180, 255] },   // #00b4ff  — cold blue
  { temp: 170, color: [0, 255, 136] },    // #3b82f6  — optimal green
  { temp: 220, color: [255, 187, 0] },    // #ffbb00  — warm yellow
  { temp: 270, color: [255, 107, 43] },   // #f87171  — hot orange
];
const TEMP_STOPS_C = [
  { temp: 38, color: [0, 180, 255] },
  { temp: 77, color: [0, 255, 136] },
  { temp: 104, color: [255, 187, 0] },
  { temp: 132, color: [255, 107, 43] },
];

function lerpColor(stops: typeof TEMP_STOPS_F, value: number): string {
  if (value <= stops[0].temp) return `rgb(${stops[0].color.join(",")})`;
  if (value >= stops[stops.length - 1].temp) return `rgb(${stops[stops.length - 1].color.join(",")})`;
  for (let i = 0; i < stops.length - 1; i++) {
    const a = stops[i], b = stops[i + 1];
    if (value >= a.temp && value <= b.temp) {
      const t = (value - a.temp) / (b.temp - a.temp);
      const r = Math.round(a.color[0] + (b.color[0] - a.color[0]) * t);
      const g = Math.round(a.color[1] + (b.color[1] - a.color[1]) * t);
      const bl = Math.round(a.color[2] + (b.color[2] - a.color[2]) * t);
      return `rgb(${r},${g},${bl})`;
    }
  }
  return `rgb(${stops[stops.length - 1].color.join(",")})`;
}

/* ═══════════════════════════════════════════════════════════════════════════
   Styles (inline objects — zero external CSS dependencies beyond globals)
   ═══════════════════════════════════════════════════════════════════════════ */

const S = {
  wrapper: {
    position: "relative" as const,
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: "0.875rem",
    padding: "1rem",
    maxWidth: 520,
  },

  /** Car silhouette overlay in the center connecting the 4 panels */
  carOverlay: {
    position: "absolute" as const,
    inset: 0,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    pointerEvents: "none" as const,
    zIndex: 1,
  },

  emptyState: {
    fontFamily: "var(--font-body)",
    fontSize: "0.85rem",
    color: "var(--text-secondary)",
    padding: "2rem",
    textAlign: "center" as const,
    letterSpacing: "0.04em",
  },
};

/* Carbon fiber repeating texture encoded as an SVG data-uri */
const CARBON_FIBER_BG = `url("data:image/svg+xml,%3Csvg width='6' height='6' xmlns='http://www.w3.org/2000/svg'%3E%3Crect width='6' height='6' fill='%23111119'/%3E%3Crect x='0' y='0' width='3' height='3' fill='%23161622' rx='0.5'/%3E%3Crect x='3' y='3' width='3' height='3' fill='%23161622' rx='0.5'/%3E%3C/svg%3E")`;

/* ═══════════════════════════════════════════════════════════════════════════
   Car Silhouette SVG — minimal top-down outline
   ═══════════════════════════════════════════════════════════════════════════ */

function CarSilhouette({ confidence }: { confidence: string }) {
  const glowOpacity = confidence === "full" ? 0.35 : confidence === "partial" ? 0.18 : 0.06;
  return (
    <svg
      width="80"
      height="120"
      viewBox="0 0 80 120"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      style={{ opacity: 0.5 }}
    >
      {/* Glow behind car */}
      <defs>
        <radialGradient id="carGlow" cx="50%" cy="50%" r="50%">
          <stop offset="0%" stopColor="var(--live)" stopOpacity={glowOpacity} />
          <stop offset="100%" stopColor="var(--live)" stopOpacity="0" />
        </radialGradient>
      </defs>
      <ellipse cx="40" cy="60" rx="38" ry="58" fill="url(#carGlow)" />

      {/* Car body outline */}
      <path
        d="M28 14 C28 8, 52 8, 52 14 L54 30 L56 44 L56 76 L54 90 L52 106 C52 112, 28 112, 28 106 L26 90 L24 76 L24 44 L26 30 Z"
        stroke="#2a2a3d"
        strokeWidth="1.5"
        fill="none"
        strokeLinejoin="round"
      />

      {/* Windshield */}
      <path
        d="M30 28 L50 28 L52 38 L28 38 Z"
        stroke="#2a2a3d"
        strokeWidth="1"
        fill="rgba(42,42,61,0.2)"
      />

      {/* Rear window */}
      <path
        d="M30 92 L50 92 L52 82 L28 82 Z"
        stroke="#2a2a3d"
        strokeWidth="1"
        fill="rgba(42,42,61,0.2)"
      />

      {/* Wheels — 4 rectangles at the corners */}
      <rect x="14" y="22" width="10" height="20" rx="3" stroke="#2a2a3d" strokeWidth="1" fill="rgba(42,42,61,0.15)" />
      <rect x="56" y="22" width="10" height="20" rx="3" stroke="#2a2a3d" strokeWidth="1" fill="rgba(42,42,61,0.15)" />
      <rect x="14" y="78" width="10" height="20" rx="3" stroke="#2a2a3d" strokeWidth="1" fill="rgba(42,42,61,0.15)" />
      <rect x="56" y="78" width="10" height="20" rx="3" stroke="#2a2a3d" strokeWidth="1" fill="rgba(42,42,61,0.15)" />

      {/* Center line */}
      <line x1="40" y1="18" x2="40" y2="102" stroke="#2a2a3d" strokeWidth="0.75" strokeDasharray="3 3" />
    </svg>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
   Main Component
   ═══════════════════════════════════════════════════════════════════════════ */

export default function TireDataGrid({ tireData, unit = "standard" }: Props) {
  if (!tireData || tireData.confidence === "none") {
    return <div style={S.emptyState}>No tire data available</div>;
  }

  const pressureUnit = unit === "metric" ? "kPa" : "PSI";
  const tempUnit = unit === "metric" ? "C" : "F";
  const confidence = tireData.confidence;

  return (
    <div style={S.wrapper} className="xpe-tire-grid">
      {/* Car silhouette in center */}
      <div style={S.carOverlay}>
        <CarSilhouette confidence={confidence} />
      </div>

      {/* 4 corner panels */}
      {CORNERS.map((key) => (
        <CornerPanel
          key={key}
          corner={key}
          data={tireData[key]}
          pressureUnit={pressureUnit}
          tempUnit={tempUnit}
          confidence={confidence}
          delay={STAGGER_DELAY[key]}
          gridRow={GRID_POS[key].row}
          gridCol={GRID_POS[key].col}
        />
      ))}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
   Corner Panel
   ═══════════════════════════════════════════════════════════════════════════ */

function CornerPanel({
  corner,
  data,
  pressureUnit,
  tempUnit,
  confidence: _confidence,
  delay,
  gridRow,
  gridCol,
}: {
  corner: CornerKey;
  data: TireCorner | null;
  pressureUnit: string;
  tempUnit: string;
  confidence: string;
  delay: number;
  gridRow: number;
  gridCol: number;
}) {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setVisible(true), delay);
    return () => clearTimeout(timer);
  }, [delay]);

  /* Which edge faces the car center? Offset corner label away from center. */
  const isLeft = corner.endsWith("F") ? corner === "LF" : corner === "LR";
  const isTop = corner === "LF" || corner === "RF";

  const panelStyle: React.CSSProperties = {
    position: "relative",
    gridRow,
    gridColumn: gridCol,
    zIndex: 2,
    backgroundImage: CARBON_FIBER_BG,
    backgroundSize: "6px 6px",
    border: "1px solid rgba(42, 42, 61, 0.7)",
    borderRadius: "var(--r-lg)",
    padding: "0.875rem 0.75rem 0.75rem",
    overflow: "hidden",
    opacity: visible ? 1 : 0,
    transform: visible ? "translateY(0) scale(1)" : `translateY(${isTop ? "-8px" : "8px"}) scale(0.96)`,
    transition: "opacity 0.5s cubic-bezier(0.22,1,0.36,1), transform 0.5s cubic-bezier(0.22,1,0.36,1)",
    // boxShadow removed (was colored glow)
  };

  if (!data) {
    return (
      <div style={{ ...panelStyle, opacity: visible ? 0.45 : 0 }}>
        <CornerLabel corner={corner} isLeft={isLeft} />
        <div style={{ fontFamily: "var(--font-body)", fontSize: "0.8125rem", color: "var(--text-secondary)", marginTop: "1.5rem" }}>
          No data
        </div>
      </div>
    );
  }

  return (
    <div style={panelStyle}>
      {/* Corner label — offset like a racing number */}
      <CornerLabel corner={corner} isLeft={isLeft} />

      {/* Hot pressure — hero value */}
      <div style={{ marginTop: "0.125rem", marginBottom: "0.625rem" }}>
        <span
          style={{
            fontFamily: "var(--font-body)", fontVariantNumeric: "tabular-nums",
            fontSize: "1.375rem",
            fontWeight: 600,
            color: "var(--live)",
            letterSpacing: "0.02em",
            textShadow: "none",
          }}
        >
          {formatPressure(data.hotPressure, pressureUnit)}
        </span>
      </div>

      {/* Cold pressure — secondary */}
      {data.coldPressure != null && (
        <div style={{ marginBottom: "0.625rem" }}>
          <span style={{ fontFamily: "var(--font-body)", fontSize: "0.82rem", fontWeight: 500, color: "var(--text-secondary)" }}>
            Cold{" "}
          </span>
          <span style={{ fontFamily: "var(--font-body)", fontVariantNumeric: "tabular-nums", fontSize: "0.8125rem", color: "var(--text-secondary)" }}>
            {formatPressure(data.coldPressure, pressureUnit)}
          </span>
        </div>
      )}

      {/* Temperature heatmap bar */}
      <TempHeatmapBar temps={data.surfaceTemps ?? data.temps} tempUnit={tempUnit} visible={visible} />

      {/* Wear degradation bars */}
      <WearSection wear={data.wear} visible={visible} />
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
   Corner Label — bold Sora, offset like a racing number
   ═══════════════════════════════════════════════════════════════════════════ */

function CornerLabel({ corner, isLeft }: { corner: string; isLeft: boolean }) {
  return (
    <div
      style={{
        fontFamily: "var(--font-body)",
        fontWeight: 500,
        fontSize: "0.82rem",
        color: "rgba(255,255,255,0.28)",
        position: "absolute" as const,
        top: "0.5rem",
        [isLeft ? "left" : "right"]: "0.625rem",
        userSelect: "none" as const,
        lineHeight: 1,
      }}
    >
      {corner}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
   Temperature Heatmap Bar — smooth gradient from actual values
   ═══════════════════════════════════════════════════════════════════════════ */

function TempHeatmapBar({
  temps,
  tempUnit,
  visible,
}: {
  temps: { L: number | null; M: number | null; R: number | null } | undefined;
  tempUnit: string;
  visible: boolean;
}) {
  if (!temps) return null;

  const stops = tempUnit === "C" ? TEMP_STOPS_C : TEMP_STOPS_F;
  const zones = [
    { key: "I", value: temps.L },
    { key: "M", value: temps.M },
    { key: "O", value: temps.R },
  ];

  /* Build a CSS gradient from the three zone temperatures */
  const colors = zones.map((z) =>
    z.value != null ? lerpColor(stops, z.value) : "rgba(42,42,61,0.6)"
  );
  const gradient = `linear-gradient(90deg, ${colors[0]} 0%, ${colors[1]} 50%, ${colors[2]} 100%)`;

  return (
    <div style={{ marginBottom: "0.625rem" }}>
      {/* Zone labels */}
      <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "0.2rem" }}>
        {zones.map((z) => (
          <span
            key={z.key}
            style={{
              fontFamily: "var(--font-body)",
              fontSize: "0.82rem",
              fontWeight: 500,
              color: "var(--text-secondary)",
              flex: 1,
              textAlign: z.key === "I" ? "left" : z.key === "O" ? "right" : "center",
            }}
          >
            {z.key}
          </span>
        ))}
      </div>

      {/* Gradient bar */}
      <div
        style={{
          height: 8,
          borderRadius: 4,
          background: gradient,
          opacity: visible ? 1 : 0,
          transform: visible ? "scaleX(1)" : "scaleX(0)",
          transformOrigin: "left",
          transition: "transform 0.7s cubic-bezier(0.22,1,0.36,1) 0.3s, opacity 0.5s ease 0.3s",
        }}
      />

      {/* Temp values below the bar */}
      <div style={{ display: "flex", justifyContent: "space-between", marginTop: "0.2rem" }}>
        {zones.map((z) => (
          <span
            key={z.key}
            style={{
              fontFamily: "var(--font-body)", fontVariantNumeric: "tabular-nums",
              fontSize: "0.8125rem",
              color: z.value != null ? lerpColor(stops, z.value) : "var(--text-secondary)",
              flex: 1,
              textAlign: z.key === "I" ? "left" : z.key === "O" ? "right" : "center",
            }}
          >
            {formatTemp(z.value, tempUnit)}
          </span>
        ))}
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
   Wear Degradation Bars — animated fill on mount
   ═══════════════════════════════════════════════════════════════════════════ */

function WearSection({
  wear,
  visible,
}: {
  wear: { L: number | null; M: number | null; R: number | null } | undefined;
  visible: boolean;
}) {
  if (!wear) return null;

  const zones = [
    { key: "I", value: wear.L },
    { key: "M", value: wear.M },
    { key: "O", value: wear.R },
  ];

  return (
    <div>
      {/* Section label */}
      <div
        style={{
          fontFamily: "var(--font-body)",
          fontSize: "0.82rem",
          fontWeight: 500,
          color: "var(--text-secondary)",
          marginBottom: "0.3rem",
        }}
      >
        Wear
      </div>

      <div style={{ display: "flex", gap: "0.375rem" }}>
        {zones.map((z) => (
          <WearBar key={z.key} value={z.value} visible={visible} />
        ))}
      </div>
    </div>
  );
}

function WearBar({
  value,
  visible,
}: {
  value: number | null | undefined;
  visible: boolean;
}) {
  const pct = value ?? 0;

  /* Gradient from green (high wear remaining) through yellow to red (worn out) */
  const barGradient =
    pct > 70
      ? "var(--flag-green)"
      : pct > 40
        ? "var(--flag-yellow)"
        : "var(--flag-red)";

  return (
    <div style={{ flex: 1, textAlign: "center" }}>
      {/* Track */}
      <div
        style={{
          height: 5,
          borderRadius: 3,
          background: "rgba(42, 42, 61, 0.5)",
          overflow: "hidden",
          marginBottom: "0.15rem",
          position: "relative" as const,
        }}
      >
        {/* Fill */}
        <div
          style={{
            width: visible ? `${pct}%` : "0%",
            height: "100%",
            borderRadius: 3,
            background: barGradient,
            transition: "width 0.9s cubic-bezier(0.22,1,0.36,1) 0.5s",
          }}
        />
      </div>

      {/* Value */}
      <span
        style={{
          fontFamily: "var(--font-body)", fontVariantNumeric: "tabular-nums",
          fontSize: "0.8125rem",
          color: "var(--text-secondary)",
        }}
      >
        {formatWear(value)}
      </span>
    </div>
  );
}
